import axios from 'axios';

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/?$/, '/');
// IoT APIs are on hub-flow-service; gateway routes /synctrack/iot/** → :3008
const IOT_BASE = `${API_ROOT}synctrack/iot`;
const SYNC_CONNECTORS_URL = `${API_ROOT}synctrack/sync-connectors`;
const MASTER_CONNECTOR_URL = `${API_ROOT}synctrack/master-connector`;

const iotClient = axios.create({
  baseURL: IOT_BASE,
  // Dense telemetry (chart/raw over long ranges) can exceed 2 minutes
  timeout: 300000,
});

iotClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem('tenantId');
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  const menuId = localStorage.getItem('iotAssetMenuId');
  if (menuId) {
    config.headers.Menu_id = menuId;
  }
  return config;
});

iotClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error?.response?.data;
    const message =
      (typeof data === 'string' && data) ||
      data?.detail ||
      data?.message ||
      data?.error ||
      error?.message ||
      'IoT API request failed';
    return Promise.reject(new Error(typeof message === 'string' ? message : JSON.stringify(message)));
  }
);

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function listTenantConnectors() {
  const tenantId = localStorage.getItem('tenantId') || '';
  const [masterRes, tokensRes] = await Promise.all([
    fetch(MASTER_CONNECTOR_URL, { headers: { Accept: 'application/json', ...authHeaders() } }),
    fetch(`${SYNC_CONNECTORS_URL}/tenant/${tenantId}`, {
      headers: { Accept: 'application/json', ...authHeaders() },
    }),
  ]);
  if (!masterRes.ok || !tokensRes.ok) {
    throw new Error('Failed to load connectors');
  }
  const masterData = await masterRes.json();
  const tokensData = await tokensRes.json();
  return (tokensData || []).map((item) => {
    const mc = (masterData || []).find(
      (m) => String(m.mcRecId) === String(item.mcId) || String(m.mcId) === String(item.mcId)
    );
    const mcName = mc?.mcName || '';
    const type = (mc?.mcConnectorType || mcName || '').toLowerCase();
    return {
      id: item.id,
      mcId: item.mcId,
      mcName: mcName || item.description || `Connector (${item.mcId})`,
      description: item.description || '',
      status: item.status,
      type,
      isAsroTrack:
        type.includes('asrotrack') || mcName.toLowerCase().replace(/\s+/g, '').includes('asrotrack'),
      isPostgres:
        type.includes('postgres') || mcName.toLowerCase().includes('postgres'),
    };
  });
}

/** AsroTrack OAuth connectors — Asset / Sensor metadata. */
export async function listAsroTrackConnectors() {
  const all = await listTenantConnectors();
  return all.filter((c) => c.isAsroTrack);
}

/** PostgreSQL connectors — IoT telemetry DB (e.g. iotmodel). */
export async function listPostgresConnectors() {
  const all = await listTenantConnectors();
  return all.filter((c) => c.isPostgres);
}

export const iotDataApi = {
  listPlants: (params) => iotClient.get('/plants', { params }),
  listAssets: (params) => iotClient.get('/assets', { params }),
  listSensors: (params) => iotClient.get('/sensors', { params }),
  getRaw: (params) => iotClient.get('/raw', { params }),
  getChart: (params) => iotClient.get('/chart', { params }),
  getDataset: (params) => iotClient.get('/dataset', { params }),
  getVibrationOverview: (params) => iotClient.get('/vibration/overview', { params }),
  getVibrationTrends: (params) => iotClient.get('/vibration/trends', { params }),
  getVibrationWaveforms: (params) => iotClient.get('/vibration/waveforms', { params }),
  getVibrationSpectra: (params) => iotClient.get('/vibration/spectra', { params }),
  getVibrationBearing: (params) => iotClient.get('/vibration/bearing', { params }),
  getVibrationCatalog: (params) => iotClient.get('/vibration/catalog', { params }),
  getVibrationExplorer: (params) => iotClient.get('/vibration/explorer', { params }),
  getVibrationContext: (params) => iotClient.get('/vibration/context', { params }),
  /** Clear + rebuild analytics tables from sensor_reading_raw on the selected IoT DB. */
  processFromRaw: (body) =>
    iotClient.post('/process-from-raw', body, { timeout: 600000 }),
  listFilterPresets: () => iotClient.get('/filter-presets'),
  saveFilterPreset: (body) => iotClient.post('/filter-presets', body),
  saveLastAppliedFilters: (body) => iotClient.post('/filter-presets/last-applied', body),
  deleteFilterPreset: (id) => iotClient.delete(`/filter-presets/${id}`),
};
